import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useActiveMandate } from '@/hooks/use-active-mandate';
import { useMandates } from '@/hooks/use-transactions';

interface MandateContextType {
  selectedMandateId: string | null;
  setSelectedMandateId: (mandateId: string) => void;
  isLoading: boolean;
}

const MandateContext = createContext<MandateContextType | undefined>(undefined);

const STORAGE_KEY = 'swiss-finance-selected-mandate';

export function MandateProvider({ children }: { children: ReactNode }) {
  const [selectedMandateId, setSelectedMandateIdState] = useState<string | null>(null);
  const { mandate: activeMandate, loading: activeMandateLoading } = useActiveMandate();
  const { data: allMandates, isLoading: mandatesLoading } = useMandates();

  // Initialize selected mandate from localStorage or active mandate
  useEffect(() => {
    if (activeMandateLoading || mandatesLoading) return;

    const storedMandateId = localStorage.getItem(STORAGE_KEY);
    
    // Check if stored mandate is valid
    if (storedMandateId && allMandates?.some(m => m.id === storedMandateId)) {
      setSelectedMandateIdState(storedMandateId);
    } else if (activeMandate) {
      // Fall back to active mandate
      setSelectedMandateIdState(activeMandate.id);
      localStorage.setItem(STORAGE_KEY, activeMandate.id);
    }
  }, [activeMandate, allMandates, activeMandateLoading, mandatesLoading]);

  const setSelectedMandateId = (mandateId: string) => {
    setSelectedMandateIdState(mandateId);
    localStorage.setItem(STORAGE_KEY, mandateId);
    
    // Broadcast change to other tabs
    window.dispatchEvent(new CustomEvent('mandate-changed', { detail: { mandateId } }));
  };

  // Listen for mandate changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        setSelectedMandateIdState(e.newValue);
      }
    };

    const handleMandateChange = (e: CustomEvent) => {
      setSelectedMandateIdState(e.detail.mandateId);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('mandate-changed', handleMandateChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('mandate-changed', handleMandateChange as EventListener);
    };
  }, []);

  const value: MandateContextType = {
    selectedMandateId,
    setSelectedMandateId,
    isLoading: activeMandateLoading || mandatesLoading,
  };

  return (
    <MandateContext.Provider value={value}>
      {children}
    </MandateContext.Provider>
  );
}

export function useMandateContext() {
  const context = useContext(MandateContext);
  if (context === undefined) {
    throw new Error('useMandateContext must be used within a MandateProvider');
  }
  return context;
}
